import { Fragment, forwardRef, memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import MethodParticleSweep from "./MethodParticleSweep.jsx";
import Eyebrow from "./Eyebrow.jsx";
import { refreshOnHeightChange } from "../scrollRefresh.js";
import usePrefersReducedMotion, { prefersReducedMotion } from "../hooks/usePrefersReducedMotion.js";
import useShortLandscape from "../hooks/useShortLandscape.js";
import "./Method.css";

gsap.registerPlugin(ScrollTrigger, SplitText, MorphSVGPlugin);

/**
 * "The 5 method": a pinned, horizontally scrolling rail modelled on
 * WeEvolveIT's framework section.
 *
 * - The rail pins for PIN_LENGTH viewports of scroll. The intro headline fades/scales
 *   in as the rail arrives, then out as the phase track slides in from the right.
 * - The track pauses with each phase's header centred before moving on.
 * - Each phase reveals char-by-char once scroll passes its threshold.
 * - The background ramps white → near-black over the last ~30% of the pin.
 * - A fixed progress bar morphs each dot into its phase letter (D/D/D/D/S).
 */

const PHASES = [
  {
    number: "01",
    name: "DISCOVER",
    tagline: "80% of tech projects fail because no one asked the right question.",
    bullets: [
      "we don't start at the solution. we start at the uncomfortable truth",
      "how your business actually runs — not the org-chart version",
      "clarity before code",
    ],
  },
  {
    number: "02",
    name: "DIAGNOSE",
    tagline: "If it can't be measured, it doesn't exist.",
    bullets: [
      "every friction, bottleneck, and silent leak — mapped",
      "an executive diagnosis few consultants will sign their name to",
      "quantified truth. zero opinions.",
    ],
  },
  {
    number: "03",
    name: "DESIGN",
    tagline: "We don't prescribe software. We prescribe outcomes.",
    bullets: [
      "every architectural decision tied to a business KPI",
      "if it doesn't move the needle, it doesn't make the plan",
      "blueprint, not buzzwords",
    ],
  },
  {
    number: "04",
    name: "DELIVER",
    tagline: "Code without adoption is debt with ego.",
    bullets: [
      "victory isn't deploy. it's your team running it without us",
      "understood without a manual. improved without permission.",
      "adoption, not delivery",
    ],
  },
  {
    number: "05",
    name: "SCALE",
    tagline: "What doesn't scale is already dying.",
    bullets: [
      "while most invoice and disappear, we stay",
      "your business changes every quarter — your tech has to match",
      "partnership, not project",
    ],
  },
];

const INTRO = {
  headline: "The 5 method",
  lead: "Technology accelerates faster every day. Most businesses fall behind. We're the AI specialists who close that gap — a five-phase method that doesn't follow the curve, it bends it. No limits. No legacy. No excuses.",
  phaseList: "Discover · Diagnose · Design · Deliver",
  evolve: "Scale",
};

// Pin schedule, in viewport heights of scroll: the intro plays first, then for
// each phase the track moves until that phase's header is centred and holds
// still for HOLD viewports before moving on (the last hold ends the pin).
const TRACK_START = 1;
const MOVE = 0.8;
const HOLD = 0.4;
const PIN_LENGTH = TRACK_START + PHASES.length * (MOVE + HOLD);
const moveStart = (i) => TRACK_START + i * (MOVE + HOLD);

// scroll progress (0..1 of the pin) at which each phase becomes active:
// halfway through its move in, so the reveal finishes as it settles
const THRESHOLDS = PHASES.map((_, i) => (moveStart(i) + MOVE / 2) / PIN_LENGTH);

// background ramp (power2.in): mid-grey at RAMP_GREY, near-black as Scale settles
const RAMP_GREY = 0.875;
const RAMP_END = (moveStart(PHASES.length - 1) + MOVE) / PIN_LENGTH;
const RAMP_START = (RAMP_GREY - Math.SQRT1_2 * RAMP_END) / (1 - Math.SQRT1_2);

const K = 0.5522847498; // cubic bezier circle constant
const clamp01 = (v) => Math.max(0, Math.min(1, v));

function circle(cx, cy, r) {
  const k = K * r;
  return `M ${cx - r} ${cy} C ${cx - r} ${cy - k}, ${cx - k} ${cy - r}, ${cx} ${cy - r} C ${cx + k} ${cy - r}, ${cx + r} ${cy - k}, ${cx + r} ${cy} C ${cx + r} ${cy + k}, ${cx + k} ${cy + r}, ${cx} ${cy + r} C ${cx - k} ${cy + r}, ${cx - r} ${cy + k}, ${cx - r} ${cy} Z`;
}

// organic blob: a circle with each quadrant radius jittered ±35%
function blob(cx, cy, r) {
  const j = () => r * (1 + (Math.random() - 0.5) * 0.7);
  const top = j(), right = j(), bottom = j(), left = j();
  const kt = K * top, kr = K * right, kb = K * bottom, kl = K * left;
  return `M ${cx - left} ${cy} C ${cx - left} ${cy - kl}, ${cx - kt} ${cy - top}, ${cx} ${cy - top} C ${cx + kt} ${cy - top}, ${cx + right} ${cy - kr}, ${cx + right} ${cy} C ${cx + right} ${cy + kr}, ${cx + kb} ${cy + bottom}, ${cx} ${cy + bottom} C ${cx - kb} ${cy + bottom}, ${cx - left} ${cy + kl}, ${cx - left} ${cy} Z`;
}

/* ------------------------------------------------------------------ */
/* DotBlob: the full stop after each headline, idly wobbling           */
/* ------------------------------------------------------------------ */

function DotBlob({ accent = false, sizeEm = 0.18 }) {
  const svgRef = useRef(null);
  const pathRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const path = pathRef.current;
    const svg = svgRef.current;
    if (!path || !svg) return;

    let call = null;
    const wobble = () => {
      const tl = gsap.timeline();
      tl.to(path, {
        morphSVG: { shape: blob(50, 50, 32), type: "rotational", shapeIndex: "auto" },
        scale: 1.4,
        svgOrigin: "50 50",
        duration: 1.5,
        ease: "sine.inOut",
      });
      tl.to(path, {
        morphSVG: { shape: circle(50, 50, 32), type: "rotational", shapeIndex: "auto" },
        scale: 1,
        duration: 1.5,
        ease: "sine.inOut",
      });
      call = gsap.delayedCall(2.5 + 3 * Math.random(), wobble);
    };
    const start = () => {
      if (!call) call = gsap.delayedCall(0.5 + 1.8 * Math.random(), wobble);
    };
    const stop = () => {
      call?.kill();
      call = null;
    };

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0 }
    );
    io.observe(svg);
    return () => {
      io.disconnect();
      stop();
      gsap.killTweensOf(path);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 100"
      aria-hidden="true"
      className="m5-dot"
      style={{ width: `${sizeEm}em`, height: `${sizeEm}em` }}
    >
      <path ref={pathRef} d={circle(50, 50, 32)} style={{ fill: accent ? "var(--m5-accent)" : "currentColor" }} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Intro: centered headline shown while the rail pins                  */
/* ------------------------------------------------------------------ */

const MethodIntro = memo(forwardRef(function MethodIntro(_, ref) {
  const [sweeping, setSweeping] = useState(false);
  // the rail is a long scroll: keep the sweep going for as long as it is on screen
  useEffect(() => {
    const rail = document.querySelector("[data-method-rail]");
    if (!rail) return;
    const io = new IntersectionObserver(([entry]) => setSweeping(entry.isIntersecting), { threshold: 0.1 });
    io.observe(rail);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {sweeping && (
        <div className="m5-sweep-layer">
          <MethodParticleSweep color="#000000" />
        </div>
      )}
      <div ref={ref} data-method-intro="" className="m5-intro" style={{ opacity: 0 }}>
        <Eyebrow section="process" />
        <h2 className="m5-intro-title">
          {INTRO.headline}
          <DotBlob />
        </h2>
        <p className="m5-intro-lead">{INTRO.lead}</p>
        <p className="m5-intro-phases">
          {[...INTRO.phaseList.split(" · "), INTRO.evolve].map((phase, i) => (
            <Fragment key={phase}>
              {i > 0 && <span className="m5-intro-sep"> · </span>}
              <span className="m5-intro-phase">{phase}</span>
            </Fragment>
          ))}
        </p>
      </div>
    </>
  );
}));

/* ------------------------------------------------------------------ */
/* Slides                                                              */
/* ------------------------------------------------------------------ */

function MethodSlide({ index, number, name, tagline, bullets }) {
  const fontSize = `clamp(38px, ${(102 / name.length).toFixed(1)}vw, ${Math.round(934 / name.length)}px)`;
  return (
    <article data-method-slide={index} aria-label={`Phase ${number} · ${name}`} className="m5-slide">
      <span data-method-watermark="" aria-hidden="true" className="m5-watermark">
        {number}
      </span>
      <div className="m5-slide-body">
        <h3 className="m5-slide-name" style={{ fontSize }}>
          <span className="m5-sr-only">{name.charAt(0) + name.slice(1).toLowerCase()}</span>
          <span data-method-name="">{name.charAt(0) + name.slice(1).toLowerCase()}</span>
          <DotBlob accent={false} />
        </h3>
        <p data-method-tagline="" className="m5-slide-tagline">
          {tagline}
        </p>
        <ul className="m5-slide-bullets">
          {bullets.map((b) => (
            <li key={b} data-method-bullet="" className="m5-slide-bullet">
              <span aria-hidden="true" className="m5-slide-dot" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

const MethodTrack = memo(forwardRef(function MethodTrack(_, ref) {
  return (
    <div ref={ref} data-method-track="" className="m5-track">
      {PHASES.map((p, i) => (
        <MethodSlide key={p.number} index={i} {...p} />
      ))}
    </div>
  );
}));

/* ------------------------------------------------------------------ */
/* Progress: dots that morph into phase letters, joined by a fill line */
/* ------------------------------------------------------------------ */

const STEPS = [
  { letter: "D", label: "Discover", cx: 20, accent: false },
  { letter: "D", label: "Diagnose", cx: 195, accent: false },
  { letter: "D", label: "Design", cx: 370, accent: false },
  { letter: "D", label: "Deliver", cx: 545, accent: false },
  { letter: "S", label: "Scale", cx: 720, accent: false },
];
const FIRST_CX = STEPS[0].cx;
const SPAN = STEPS[STEPS.length - 1].cx - FIRST_CX;
const SEGMENTS = STEPS.slice(0, -1).map((s, i) => ({ x1: s.cx + 19, x2: STEPS[i + 1].cx - 19 }));

// idle dot: r=9 circle with a tiny hole so it morphs cleanly into D/E counters
const dotShape = (cx) => `${circle(cx, 50, 9)} ${circle(cx, 50, 0.4)}`;

function letterS(cx) {
  const l = cx - 15, r = cx + 15, ls = l + 8, rs = r - 8;
  return `M ${l} 25 L ${r} 25 L ${r} 35 L ${ls} 35 L ${ls} 45 L ${r} 45 L ${r} 75 L ${l} 75 L ${l} 65 L ${rs} 65 L ${rs} 55 L ${l} 55 Z ${circle(cx, 50, 0.4)}`;
}

function letterD(cx) {
  const l = cx - 15, r = cx + 15, stem = l + 8;
  const k = K * (r - stem);
  const outer = `M ${l} 75 L ${l} 25 L ${stem} 25 C ${stem + k} 25, ${r} ${50 - k}, ${r} 50 C ${r} ${50 + k}, ${stem + k} 75, ${stem} 75 L ${l} 75 Z`;
  const top = 35, bottom = 65, ir = r - 10;
  const rad = Math.min((bottom - top) / 2, ir - stem);
  const ik = K * rad, ix = ir - rad;
  const inner = `M ${stem} ${top} L ${ix} ${top} C ${ix + ik} ${top}, ${ir} ${50 - ik}, ${ir} 50 C ${ir} ${50 + ik}, ${ix + ik} ${bottom}, ${ix} ${bottom} L ${stem} ${bottom} Z`;
  return `${outer} ${inner}`;
}

function MethodProgress({ progress, activePhase }) {
  const rootRef = useRef(null);
  const pathRefs = useRef([]);
  const labelRefs = useRef([]);
  const prevPhase = useRef(-1);
  const activeRef = useRef(-1);

  useEffect(() => {
    activeRef.current = activePhase;
  }, [activePhase]);

  // centre the labels via xPercent so GSAP's y tween doesn't clobber it
  useLayoutEffect(() => {
    gsap.set(labelRefs.current.filter(Boolean), { xPercent: -50, autoAlpha: 0, y: 4 });
  }, []);

  // morph dots ↔ letters as phases activate / deactivate
  useEffect(() => {
    const paths = pathRefs.current.filter(Boolean);
    const labels = labelRefs.current.filter(Boolean);
    if (paths.length !== 5 || labels.length !== 5) return;
    const prev = prevPhase.current;

    STEPS.forEach((step, i) => {
      const on = i <= activePhase;
      if (on === i <= prev) return;
      const shape = on ? (step.letter === "S" ? letterS(step.cx) : letterD(step.cx)) : dotShape(step.cx);
      gsap.killTweensOf(paths[i]);
      gsap.to(paths[i], {
        morphSVG: { shape, type: "rotational", shapeIndex: "auto" },
        scale: 1,
        duration: 0.7,
        ease: "power3.inOut",
      });
      gsap.to(labels[i], {
        autoAlpha: on ? 1 : 0,
        y: on ? 0 : 4,
        duration: 0.45,
        delay: on ? 0.22 : 0,
        ease: "power2.out",
        overwrite: "auto",
      });
    });
    prevPhase.current = activePhase;
  }, [activePhase]);

  // idle wobble on a random not-yet-active dot
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const paths = pathRefs.current.filter(Boolean);
    const root = rootRef.current;
    if (paths.length !== 5 || !root) return;

    let call = null;
    const wobble = () => {
      const idle = [];
      for (let i = 0; i < STEPS.length; i++) if (i > activeRef.current) idle.push(i);
      if (idle.length) {
        const i = idle[Math.floor(Math.random() * idle.length)];
        const { cx } = STEPS[i];
        const tl = gsap.timeline();
        tl.to(paths[i], {
          morphSVG: { shape: `${blob(cx, 50, 9)} ${circle(cx, 50, 0.4)}`, type: "rotational", shapeIndex: "auto" },
          scale: 1.6,
          svgOrigin: `${cx} 50`,
          duration: 1.3,
          ease: "sine.inOut",
        });
        tl.to(paths[i], {
          morphSVG: { shape: dotShape(cx), type: "rotational", shapeIndex: "auto" },
          scale: 1,
          duration: 1.3,
          ease: "sine.inOut",
        });
      }
      call = gsap.delayedCall(2.5 + 3 * Math.random(), wobble);
    };
    const start = () => {
      if (!call) call = gsap.delayedCall(1.5 + 1.5 * Math.random(), wobble);
    };
    const stop = () => {
      call?.kill();
      call = null;
    };

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0 }
    );
    io.observe(root);
    return () => {
      io.disconnect();
      stop();
    };
  }, []);

  const visible = clamp01((progress - (THRESHOLDS[0] - 0.1)) / 0.1);
  const lift = (1 - visible) * 14;
  // piecewise so the line reaches dot i exactly as phase i activates
  let seg = 0;
  while (seg < THRESHOLDS.length - 2 && progress >= THRESHOLDS[seg + 1]) seg++;
  const fill = clamp01(
    (seg + clamp01((progress - THRESHOLDS[seg]) / (THRESHOLDS[seg + 1] - THRESHOLDS[seg]))) / (STEPS.length - 1)
  );
  const head = FIRST_CX + fill * SPAN;
  const tail = Math.min(26, head - FIRST_CX);

  return (
    <div
      ref={rootRef}
      className="m5-progress"
      style={{ opacity: visible, transform: `translateX(-50%) translateY(${lift}px)` }}
    >
      <svg viewBox="0 0 740 100" className="m5-progress-svg" aria-label="method progress" role="img">
        {SEGMENTS.map((s, i) => (
          <rect key={`bg-${i}`} x={s.x1} y={49.25} width={s.x2 - s.x1} height={1.5} style={{ fill: "var(--method-border)" }} />
        ))}
        {SEGMENTS.map((s, i) => {
          const x = Math.min(Math.max(head, s.x1), s.x2);
          return (
            <rect key={`fill-${i}`} x={s.x1} y={49.25} width={Math.max(0, x - s.x1)} height={1.5} style={{ fill: "var(--method-fg)" }} />
          );
        })}
        {fill > 0.005 && fill < 0.995 && (
          <g pointerEvents="none">
            <defs>
              <linearGradient id="m5-spark-tail" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" style={{ stopColor: "var(--method-fg)", stopOpacity: 0 }} />
                <stop offset="100%" style={{ stopColor: "var(--method-fg)", stopOpacity: 0.9 }} />
              </linearGradient>
            </defs>
            <rect x={head - tail} y={48.25} width={tail} height={3.5} rx={1} fill="url(#m5-spark-tail)" />
            <circle cx={head} cy={50} r={2.4} style={{ fill: "var(--method-fg)" }} />
          </g>
        )}
        {STEPS.map((s, i) => (
          <path
            key={i}
            ref={(el) => (pathRefs.current[i] = el)}
            d={dotShape(s.cx)}
            fillRule="evenodd"
            style={{ fill: s.accent ? "var(--m5-accent)" : "var(--method-fg)" }}
          />
        ))}
      </svg>
      <div className="m5-progress-labels">
        {STEPS.map((s, i) => (
          <span
            key={i}
            ref={(el) => (labelRefs.current[i] = el)}
            className="m5-progress-label"
            style={{ left: `${(s.cx / 740) * 100}%`, color: s.accent ? "var(--m5-accent)" : "var(--method-fg)" }}
          >
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rail: pin + horizontal scrub                                        */
/* ------------------------------------------------------------------ */

// Track x that puts slide i's header (its letters + dot, not the padded h3
// box) in the middle of the rail. Measured against the track's own rect so
// the track's current translate cancels out.
function headerCenterX(track, rail, i) {
  const fallback = window.innerWidth * (0.5 - 0.7 * i - 0.35);
  const header = track.querySelector(`[data-method-slide="${i}"] .m5-slide-name`);
  if (!header) return fallback;

  let left = Infinity;
  let right = -Infinity;
  const add = (rect) => {
    if (rect.width === 0) return;
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  };

  const range = document.createRange();
  const walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent.trim() || node.parentElement.closest(".m5-sr-only")) continue;
    range.selectNodeContents(node);
    add(range.getBoundingClientRect());
  }
  header.querySelectorAll(".m5-dot").forEach((el) => add(el.getBoundingClientRect()));
  if (!Number.isFinite(left)) return fallback;

  const center = (left + right) / 2 - track.getBoundingClientRect().left;
  return rail.clientWidth / 2 - center;
}

function MethodRail({ fadeToTransparent }) {
  const railRef = useRef(null);
  const trackRef = useRef(null);
  const introRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activePhase, setActivePhase] = useState(-1);
  // reduced motion and landscape phones get the plain vertical stack: the pinned rail needs a viewport-high stage
  const reducedMotion = usePrefersReducedMotion();
  const shortLandscape = useShortLandscape();
  const reduced = reducedMotion || shortLandscape;

  useLayoutEffect(() => {
    if (reduced) return;
    const rail = railRef.current;
    const track = trackRef.current;
    const intro = introRef.current;
    if (!rail || !track || !intro) return;

    const wrapper = rail.closest("[data-method]");
    const fgMix = gsap.utils.interpolate("#171717", "#F0F0F8");
    const borderMix = gsap.utils.interpolate("rgba(0,0,0,0.1)", "rgba(255,255,255,0.1)");
    const easeIn = gsap.parseEase("power2.in");
    const splits = [];
    const slides = [];

    const ctx = gsap.context(() => {
      PHASES.forEach((_, i) => {
        const slide = track.querySelector(`[data-method-slide="${i}"]`);
        const name = slide?.querySelector("[data-method-name]");
        const tagline = slide?.querySelector("[data-method-tagline]");
        if (!name || !tagline) return;
        const bullets = Array.from(slide.querySelectorAll("[data-method-bullet]"));
        // "hidden" keeps aria-label off the role-less span; the .m5-sr-only copy carries the name
        const split = SplitText.create(name, { type: "chars", charsClass: "m5-char", aria: "hidden" });
        splits.push(split);
        gsap.set(name, { opacity: 1 });
        if (i === 0) {
          gsap.set([split.chars, tagline, bullets], { y: 0, opacity: 1 });
        } else {
          gsap.set(split.chars, { y: 24, opacity: 0 });
          gsap.set([tagline, bullets], { y: 12, opacity: 0 });
        }
        slides.push({ chars: split.chars, tagline, bullets });
      });

      const shown = [true, false, false, false, false];
      let shownProgress = 0;
      let lastEase = -1;
      let lastAlpha = -1;
      const reveal = (i) => {
        const s = slides[i];
        if (!s) return;
        gsap.to(s.chars, { y: 0, opacity: 1, duration: 0.8, stagger: 0.06, ease: "expo.out", overwrite: "auto" });
        gsap.to(s.tagline, { y: 0, opacity: 1, duration: 0.8, ease: "expo.out", delay: 0.15, overwrite: "auto" });
        gsap.to(s.bullets, { y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: "expo.out", delay: 0.3, overwrite: "auto" });
      };
      const conceal = (i) => {
        const s = slides[i];
        if (!s) return;
        gsap.to(s.chars, { y: 24, opacity: 0, duration: 0.3, ease: "power3.in", overwrite: "auto" });
        gsap.to(s.tagline, { y: 12, opacity: 0, duration: 0.3, ease: "power3.in", overwrite: "auto" });
        gsap.to(s.bullets, { y: 12, opacity: 0, duration: 0.3, ease: "power3.in", overwrite: "auto" });
      };

      // intro: fade/scale in as the rail arrives, hold, then out as slides enter
      gsap.set(intro, { opacity: 0, scale: 0.95 });
      gsap
        .timeline({ scrollTrigger: { trigger: rail, start: "top bottom", end: "+=200%", scrub: 1.5 } })
        .to(intro, { opacity: 1, scale: 1, ease: "none", duration: 50 })
        .to(intro, { opacity: 1, scale: 1, duration: 35 })
        .to(intro, { opacity: 0, scale: 1.05, ease: "none", duration: 15 });

      const pin = gsap
        .timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: rail,
            start: "top top",
            end: `+=${PIN_LENGTH * 100}%`,
            pin: true,
            scrub: 1.5,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const p = self.progress;
              // the progress bar is ~740 units wide: finer steps than this aren't visible, but each one re-renders it
              if (p === 0 || p === 1 || Math.abs(p - shownProgress) >= 0.0015) {
                shownProgress = p;
                setProgress(p);
              }

              let active = -1;
              for (let i = 0; i < THRESHOLDS.length; i++) if (p >= THRESHOLDS[i]) active = i;
              setActivePhase(active);

              for (let i = 1; i < THRESHOLDS.length; i++) {
                const past = p >= THRESHOLDS[i];
                if (past && !shown[i]) {
                  shown[i] = true;
                  reveal(i);
                } else if (!past && shown[i]) {
                  shown[i] = false;
                  conceal(i);
                }
              }

              if (wrapper) {
                const e = easeIn(clamp01((p - RAMP_START) / (RAMP_END - RAMP_START)));
                const alpha = fadeToTransparent ? 1 - clamp01((p - 0.95) / 0.05) : 1;
                // outside the ramp (all the holds) nothing changes; writing custom properties on <html>
                // restyles the whole document, so skip it unless the colours actually moved
                if (e === lastEase && alpha === lastAlpha) return;
                lastEase = e;
                lastAlpha = alpha;
                const r = Math.round(255 - 249 * e);
                const g = Math.round(255 - 247 * e);
                const b = Math.round(255 - 239 * e);
                wrapper.style.setProperty("--method-bg", `rgba(${r}, ${g}, ${b}, ${alpha})`);
                wrapper.style.setProperty("--method-fg", fgMix(e));
                wrapper.style.setProperty("--method-border", borderMix(e));
                // the navbar follows the same ramp so its colours never lag the background
                const root = document.documentElement.style;
                root.setProperty("--nav-ink", fgMix(e));
                root.setProperty("--nav-paper", `rgb(${r}, ${g}, ${b})`);
                root.setProperty("--nav-invert", e.toFixed(3));
                wrapper.dataset.navTheme = alpha > 0.5 ? "live" : "light";
              }
            },
          },
        });

      // timeline time is in viewports of scroll; the gaps between moves are the holds
      PHASES.forEach((_, i) => {
        const to = { x: () => headerCenterX(track, rail, i), duration: MOVE, ease: "power1.inOut" };
        if (i === 0) pin.fromTo(track, { x: () => window.innerWidth }, to, moveStart(0));
        else pin.to(track, to, moveStart(i));
      });
      // empty tail so the timeline spans the whole pin (the last hold)
      pin.to({}, { duration: HOLD }, moveStart(PHASES.length - 1) + MOVE);
    }, rail);

    // Sections above (e.g. the hero frame) change height while scrolling, which
    // leaves the pin's start/end stale and makes the rail jump when it releases.
    const unwatch = refreshOnHeightChange(wrapper?.parentElement);

    return () => {
      unwatch();
      ["--nav-ink", "--nav-paper", "--nav-invert"].forEach((v) => document.documentElement.style.removeProperty(v));
      ctx.revert();
      splits.forEach((s) => s.revert());
    };
  }, [reduced, fadeToTransparent]);

  if (reduced) {
    return (
      <div ref={railRef} data-method-rail="" data-reduced-motion="true" className="m5-rail m5-rail--static">
        <div className="m5-static-head">
          <h2 className="m5-static-title">{INTRO.headline}.</h2>
          <p className="m5-static-sub">five phases to scale</p>
        </div>
        <MethodTrack ref={trackRef} />
      </div>
    );
  }

  return (
    <div ref={railRef} data-method-rail="" className="m5-rail">
      <MethodIntro ref={introRef} />
      <MethodTrack ref={trackRef} />
      <MethodProgress progress={progress} activePhase={activePhase} />
    </div>
  );
}

/**
 * @param {boolean} [fadeToTransparent=false] The reference fades the dark
 *   background to transparent over the last 5% of the pin so it blends into
 *   a dark page. Leave off on a light page, or the text flashes white-on-white.
 */
export default function Method({ fadeToTransparent = false }) {
  return (
    <div data-method="" id="process" className="m5">
      <MethodRail fadeToTransparent={fadeToTransparent} />
    </div>
  );
}
